/** Utilidades puras (testáveis em Node, sem DOM). */

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

export const dbToGain = (db: number): number => Math.pow(10, db / 20);
export const gainToDb = (g: number): number =>
  g <= 0 ? -Infinity : 20 * Math.log10(g);

let counter = 0;
export function uid(prefix = 'id'): string {
  counter = (counter + 1) % 1_000_000;
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${counter.toString(36)}_${rnd}`;
}

/** 00:00 / 00:00.0 / 1:02:03 */
export function formatTime(sec: number, withMs = false): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec % 1) * 10);
  const base =
    h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return withMs ? `${base}.${ms}` : base;
}

export function formatMs(ms: number): string {
  const sign = ms < 0 ? '−' : '+';
  const abs = Math.abs(ms);
  return `${sign}${abs < 100 ? abs.toFixed(0) : Math.round(abs)} ms`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Extrai o VIDEO_ID de qualquer formato comum de link do YouTube.
 * Aceita: watch?v=, youtu.be/, shorts/, embed/, live/, /v/, e o ID puro.
 * Retorna null quando não é possível identificar (sem inventar ID).
 */
export function extractYoutubeId(input: string): string | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;

  // ID puro colado pelo usuário (11 chars, alfabeto do YouTube)
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;

  let url: URL;
  try {
    url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '').replace(/^m\./, '');
  const ok =
    host === 'youtube.com' ||
    host === 'youtube-nocookie.com' ||
    host === 'youtu.be' ||
    host === 'music.youtube.com';
  if (!ok) return null;

  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
  }

  const v = url.searchParams.get('v');
  if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

  const parts = url.pathname.split('/').filter(Boolean);
  const known = ['shorts', 'embed', 'live', 'v'];
  if (parts.length >= 2 && known.includes(parts[0])) {
    const id = parts[1];
    return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
  }
  return null;
}

export function youtubeThumb(videoId: string, size: 'hq' | 'mq' = 'hq'): string {
  return `https://i.ytimg.com/vi/${videoId}/${size}default.jpg`;
}

export function oEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(
    `https://www.youtube.com/watch?v=${videoId}`
  )}`;
}

/** Nome de nota a partir de frequência (A4 = 440 Hz). */
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export function freqToNote(freq: number): { note: string; cents: number; midi: number } | null {
  if (!freq || freq <= 0) return null;
  const midiFloat = 69 + 12 * Math.log2(freq / 440);
  const midi = Math.round(midiFloat);
  const cents = Math.round((midiFloat - midi) * 100);
  const octave = Math.floor(midi / 12) - 1;
  const name = NOTE_NAMES[((midi % 12) + 12) % 12];
  return { note: `${name}${octave}`, cents, midi };
}

export function noteToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function median(values: number[]): number {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Regressão linear simples: y = a*x + b. Retorna inclinação `a`. */
export function linearSlope(xs: number[], ys: number[]): { slope: number; intercept: number; r2: number } | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return null;
  let sx = 0, sy = 0, sxx = 0, sxy = 0, syy = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i]; sy += ys[i];
    sxx += xs[i] * xs[i]; sxy += xs[i] * ys[i]; syy += ys[i] * ys[i];
  }
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const slope = (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  const sst = syy - (sy * sy) / n;
  const ssr = slope * (sxy - (sx * sy) / n);
  const r2 = Math.abs(sst) < 1e-12 ? 1 : ssr / sst;
  return { slope, intercept, r2 };
}
